import {
  Box,
  GlobalStyles,
  Paper,
  Stack,
  Typography,
  useTheme,
} from '@mui/material'
import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import LoadingWithBackdrop from '~/components/loadingWithBackdrop'
import PageErrorBoundary from '~/components/pageErrorBoundary'
import { EChatStatus } from '~/features/agents/models'
import { CALENDLY_PORTAL_ID } from '~/features/chatbot/constants'
import useEmbeddedChatContext from '~/features/chatbot/hooks/useEmbeddedChatContext'
import useEmbeddedIconStyles from '~/features/chatbot/hooks/useEmbeddedIconStyles'
import useEmbeddedPopup from '~/features/chatbot/hooks/useEmbeddedPopup'
import useIsEmbeddedChat from '~/features/chatbot/hooks/useIsEmbeddedChat'
import usePopulateChatbot from '~/features/chatbot/hooks/usePopulateChatbot'
import PartnerThemeWrapper from '~/features/partners/components/partnerThemeWrapper'
import { EChatbotTheme } from '~/features/settings/models'
import useDismissAllToasts from '~/hooks/useDismissAllToasts'
import Footer from '~/layouts/chatbot/footer'
import { getLayoutColors } from '~/layouts/chatbot/utils'
import PageLayout from '~/layouts/page'
import { EClientPath } from '~/models/api'
import { store } from '~/store'

function EmbeddedStyles({ isEmbedded }: { isEmbedded: boolean }) {
  return (
    isEmbedded && (
      <GlobalStyles
        styles={{
          html: {
            zoom: 0.8,
            height: '100%',
            overflow: 'hidden',
          },
          body: {
            height: '100%',
            overflow: 'hidden',
          },
          '#root': {
            height: '100%',
          },
        }}
      />
    )
  )
}

function ChatBotLayout() {
  useDismissAllToasts()
  const [showLoading, setShowLoading] = useState(true)
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const chatbot = store((state) => state.chatbot)
  const theme = useTheme()
  const { isAgentLookupFatalError, isLoading } = usePopulateChatbot()
  const { isEmbedded, isMinimized } = useIsEmbeddedChat()

  const showFooter = isEmbedded
    ? !isMinimized && chatbot.agent.showBranding
    : chatbot.agent.showBranding

  const { wrapperBackgroundColor, containerBackgroundColor } = getLayoutColors(
    chatbot.layout,
    theme,
  )

  useEmbeddedPopup({ isLoading })
  useEmbeddedIconStyles({ isLoading })
  useEmbeddedChatContext()

  // bypass footRef weird behavior ( needed an extra render to get the correct height )
  useEffect(() => {
    if (!isLoading && !isAgentLookupFatalError && showLoading) {
      setShowLoading(false)
    }
  }, [isLoading, isAgentLookupFatalError, showLoading])

  // check chatStatus availability
  useEffect(() => {
    if (
      !isLoading &&
      !isAgentLookupFatalError &&
      chatbot.agent.chatStatus !== EChatStatus.active &&
      !isEmbedded
    ) {
      navigate(EClientPath.notFound, {
        state: { from: `${pathname}${search}` },
      })
    }
  }, [
    isLoading,
    isAgentLookupFatalError,
    chatbot.agent.chatStatus,
    navigate,
    isEmbedded,
    pathname,
    search,
  ])

  if (isLoading || (isAgentLookupFatalError && !isEmbedded)) {
    return (
      <>
        <EmbeddedStyles isEmbedded={isEmbedded} />
        <LoadingWithBackdrop show />
      </>
    )
  }

  // isAgentLookupFatalError explicitly (initial load failed with no cached
  // agent), not just the chatStatus check below — chatStatus defaults to
  // undefined (getDefaultAgentData) whenever the agent lookup never actually
  // populated the store. Background refetch errors keep cached data and must
  // not unmount a live session (isLoadingError only, not isRefetchError).
  if (
    (chatbot.agent.chatStatus !== EChatStatus.active ||
      isAgentLookupFatalError) &&
    isEmbedded
  ) {
    return (
      <>
        <EmbeddedStyles isEmbedded={isEmbedded} />
        <Stack
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            height: isEmbedded ? '100%' : '100vh',
            padding: 3,
          }}
        >
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            Provided agent ID is inactive or doesn&apos;t exist.
          </Typography>
        </Stack>
      </>
    )
  }

  return (
    <PartnerThemeWrapper>
      <EmbeddedStyles isEmbedded={isEmbedded} />
      <PageLayout
        sx={{
          display: 'grid',
          backgroundColor: wrapperBackgroundColor,
          ...(isEmbedded
            ? {
                height: '100%',
                minHeight: '100%',
                maxHeight: '100%',
              }
            : {
                minHeight: '100dvh',
                maxHeight: '100dvh',
              }),
        }}
        containerProps={{
          sx: {
            height: isEmbedded ? '100%' : '100dvh',
            maxWidth: { xxxs: '100%', md: 800 },
            minWidth: 300,
            paddingInline: { xxxs: 0 },
            overflow: 'hidden',
          },
        }}
      >
        <Paper
          elevation={
            chatbot.layout.selection.theme === EChatbotTheme.linkView ? 0 : 3
          }
          sx={{
            backgroundColor: containerBackgroundColor,
            height: '100%',
          }}
        >
          <Stack
            spacing={0}
            sx={{
              height: '100%',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                minHeight: 0,
                width: '100%',
                overflow: 'hidden',
              }}
            >
              <PageErrorBoundary>
                <Outlet />
              </PageErrorBoundary>
            </Box>
            {showFooter && <Footer />}
          </Stack>
        </Paper>
      </PageLayout>

      <Box
        id={CALENDLY_PORTAL_ID}
        sx={{
          '& .calendly-overlay .calendly-popup': {
            maxHeight: '100vh',
            minWidth: '100%',
          },
        }}
      />
    </PartnerThemeWrapper>
  )
}

export default ChatBotLayout
